import invariant from 'tiny-invariant';
import { qs, isElement, bindMethod, nextUid, qsa, evaluate, isPlainObject, noop, warn, Events, createSequence } from 'yuzu-utils';

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

// A type of promise-like that resolves synchronously and supports only one observer
var _iteratorSymbol =
/*#__PURE__*/
typeof Symbol !== "undefined" ? Symbol.iterator || (Symbol.iterator =
/*#__PURE__*/
Symbol("Symbol.iterator")) : "@@iterator"; // Asynchronously iterate through an object's values
var _asyncIteratorSymbol =
/*#__PURE__*/
typeof Symbol !== "undefined" ? Symbol.asyncIterator || (Symbol.asyncIterator =
/*#__PURE__*/
Symbol("Symbol.asyncIterator")) : "@@asyncIterator"; // Asynchronously iterate on a value using it's async iterator if present, or its synchronous iterator if missing

function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }

  if (result && result.then) {
    return result.then(void 0, recover);
  }

  return result;
} // Asynchronously await a promise and pass the result to a finally continuation

var LISTENER_REGEXP = /^([^ ]+)(?: (.+))?$/;
var objDiff = noop;

if (process.env.NODE_ENV !== 'production') {
  objDiff = function objDiff(match, obj, msg) {
    var keys = Object.keys(match);
    var keyStr = keys.length > 0 ? keys.join(', ') : 'no keys';
    Object.keys(obj).forEach(function (k) {
      if (keys.indexOf(k) === -1) {
        msg(k, keyStr);
      }
    });
  };
}
/**
 * `Component` is an extensible class constructor which provides the building block of Yuzu component system.
 *
 * It inherits methods from [**Events**](/packages/yuzu-utils/api/events).
 *
 * ```js
 * const instance = new Component({ ... })
 * ```
 *
 * > **Lifecycle**
 * >
 * > | stage    | hooks     |
 * > |----------| --------- |
 * > | `create` | `created` |
 *
 * @class
 * @param {object} [options={}] Instance options
 * @property {boolean} $active `true` if the instance is mounted and initialized
 * @property {object} options Instance options (see [defaultOptions](#defaultOptions))
 * @property {object} state Instance state (see [setState](#setState))
 * @property {Element} $el The instance root DOM element (see [mount](#mount))
 * @property {Object.<string, Element>} $els  Object mapping references to component's child DOM elements (see `selectors` below)
 * @property {{ string: Component }} $refs Object mapping references to child components (see [setRef](#setRef))
 * @property {object} selectors Object mapping a child element's reference name and a CSS selector or custom function
 * @property {Object.<string, function|string>} listeners Object mapping DOM listeners and handlers (see [setListener](#setListener))
 * @property {Object.<string, function|string>} actions Object mapping state keys and functions to executed on state update
 * @returns {Component}
 */


var Component =
/*#__PURE__*/
function (_Events) {
  _inheritsLoose(Component, _Events);

  // public $$getTree?: Record<string, any>;

  /**
   * ```js
   * this.readyState(state, prevState)
   * ```
   *
   * Optional method used to delay the execution of [`ready`](#ready) after the state satisfies to a given condition.
   *
   * Will be executed at every state change until it returns `true`.
   *
   * @name readyState
   * @public
   * @type {function}
   * @param {object} state The current state
   * @param {object} prevState The previous state
   * @returns {boolean}
   */

  /**
   * Component constructor
   */
  function Component(options) {
    var _this;

    if (options === void 0) {
      options = {};
    }

    _this = _Events.call(this) || this;
    _this.$warn = warn(_assertThisInitialized(_this));
    var defaultOptionsFn = _this.constructor.defaultOptions;
    var defaultOptions;

    if (typeof defaultOptionsFn === 'function') {
      if (process.env.NODE_ENV !== 'production') {
        _this.$warn('the static property `defaultOptions` is deprecated. Please move the method to the instance');
      }

      defaultOptions = defaultOptionsFn(_assertThisInitialized(_this));
    } else {
      defaultOptions = _this.defaultOptions(_assertThisInitialized(_this)) || {};
    }

    if (process.env.NODE_ENV !== 'production') {
      objDiff(defaultOptions, options, function (k, keys) {
        return _this.$warn("Option " + k + "\" has been discarded because it is not defined in component's defaultOptions. Accepted keys are: " + keys);
      });
    }

    var entries = Object.entries(defaultOptions);
    _this.options = entries.reduce(function (opts, _ref) {
      var key = _ref[0],
          value = _ref[1];
      var v = options[key] !== undefined ? options[key] : value;

      if (typeof v === 'function' && !Component.isComponent(v)) {
        v = v.bind(_assertThisInitialized(_this));
      }

      opts[key] = v; // eslint-disable-line no-param-reassign

      return opts;
    }, {});
    _this.$active = false;
    _this.state = {}; // DOM references

    _this.$els = {}; // sub components references

    _this.$refs = {};
    _this.$refsStore = new Map();
    _this.$listeners = new Map();

    _this.created();

    return _this;
  }
  /**
   * ```js
   * Component.isComponent(obj)
   * ```
   *
   * Checks whether the passed-in value is a Component constructor.
   *
   * @static
   * @param {*} value
   * @returns {boolean}
   */


  Component.isComponent = function isComponent(value) {
    return !!(value && value.YUZU_COMPONENT);
  };

  var _proto = Component.prototype;

  _proto.defaultOptions = function defaultOptions() {
    return {};
  }
  /**
   * ```js
   * mount(el, [state])
   * ```
   *
   * Mounts a component instance on a DOM element and initializes it.
   *
   * ?> To prevent initialization and just mount the component set `state` to `null`.
   *
   * > **Lifecycle**
   * >
   * > | stage    | hooks     |
   * > |----------| --------- |
   * > | `mount` | `beforeMount` <sup>(1)</sup>, `mounted` |
   *
   * 1. Just after attaching the root element (`this.$el`) but before any listener and selector registration.
   *
   * @param {string|Element} el Component's root element
   * @param {object|null} [state={}] Initial state
   * @returns {Component}
   */
  ;

  _proto.mount = function mount(el, state) {
    var _this2 = this;

    if (state === void 0) {
      state = {};
    }

    !(this.$el === undefined) ? process.env.NODE_ENV !== "production" ? invariant(false, 'Component is already mounted') : invariant(false) : void 0;
    !(this.detached === undefined) ? process.env.NODE_ENV !== "production" ? invariant(false, 'You cannot mount a detached component. Please use `init` instead') : invariant(false) : void 0;
    var $el = typeof el === 'string' ? qs(el) : el;

    if (!isElement($el)) {
      // fail silently (kinda...);
      if (process.env.NODE_ENV !== 'production') {
        this.$warn('Element is not a DOM element', $el);
      }

      return this;
    }

    this.$el = $el; // eslint-disable-line no-multi-assign

    this.beforeMount();

    if (this.selectors) {
      Object.entries(this.selectors).forEach(function (_ref2) {
        var key = _ref2[0],
            selector = _ref2[1];

        if (typeof selector === 'function') {
          _this2.$els[key.replace('[]', '')] = selector(_this2.$el, _this2.options);
          return;
        }

        if (!key.endsWith('[]')) {
          var _el = _this2.findNode(selector);

          if (_el) {
            _this2.$els[key] = _el;
          }
        } else {
          _this2.$els[key.slice(0, -2)] = _this2.findNodes(selector);
        }
      });
    }

    if (this.listeners) {
      Object.entries(this.listeners).forEach(function (_ref3) {
        var def = _ref3[0],
            handler = _ref3[1];

        _this2.setListener(def, bindMethod(_this2, handler));
      });
    }

    if (state) {
      this.init(state);
    }

    this.mounted();
    return this;
  }
  /**
   * ```js
   * init([state])
   * ```
   * Initializes the component instance.
   *
   * > **Lifecycle**
   * >
   * > | stage    | hooks     |
   * > |----------| --------- |
   * > | `init` | `initialize`, `ready` |
   *
   * @param {object|null} [state={}] Initial state
   * @returns {Component}
   */
  ;

  _proto.init = function init(state) {
    var _this3 = this;

    if (state === void 0) {
      state = {};
    }

    !(this.detached === true || isElement(this.$el)) ? process.env.NODE_ENV !== "production" ? invariant(false, 'component instance not mounted') : invariant(false) : void 0;
    var $el = this.$el; // initialization placeholder

    if ($el && $el.hasAttribute(Component.YUZU_DATA_ATTR)) {
      if (process.env.NODE_ENV !== 'production') {
        this.$warn("Element is already initialized... skipping", $el);
      }

      return this;
    }

    this.$uid = nextUid();

    if ($el) {
      $el.setAttribute(Component.YUZU_DATA_ATTR, '');
    }

    this.initialize();

    if (this.actions) {
      Object.entries(this.actions).forEach(function (_ref4) {
        var key = _ref4[0],
            method = _ref4[1];

        _this3.on("change:" + key, bindMethod(_this3, method));
      });
    }

    var initialState = Object.assign({}, this.state, state);
    this.replaceState(initialState);
    this.$active = true;

    if (this.readyState) {
      // is it a promise ?
      var watcher = function watcher(current, prev) {
        if (!_this3.readyState) {
          return;
        }

        if (_this3.readyState(current, prev)) {
          _this3.off('change:*', watcher);

          _this3.ready();
        }
      };

      this.on('change:*', watcher);
      return this;
    }

    this.ready();
    return this;
  }
  /* eslint-disable @typescript-eslint/no-empty-function */

  /**
   * Lifecycle hook called on instance creation.
   *
   * At this stage just the instance options (`this.options`) are initialized.
   *
   *  Overwrite this method with custom logic in your components.
   *
   * ?> Use this hook to tap as early as possible into the component's properties. For example to set a dynamic initial state.
   */
  ;

  _proto.created = function created() {}
  /**
   * Lifecycle hook called just before mounting the instance onto the root element.
   *
   * At this stage the component is already attached to its root DOM element.
   *
   * Overwrite this method with custom logic in your components.
   */
  ;

  _proto.beforeMount = function beforeMount() {}
  /**
   * Lifecycle hook called when the instance gets mounted onto a DOM element.
   *
   * At this stage both children elements (`this.$els.*`) and event listeners configured in `this.listeners` have been setup.
   *
   * Overwrite this method with custom logic in your components.
   *
   * ?> Use this method when you need to work with the DOM or manage any side-effect that requires the component to be into the DOM.
   */
  ;

  _proto.mounted = function mounted() {}
  /**
   * Lifecycle hook called before instance initialization.
   *
   * At this stage the state and state listeners are not yet been initialized.
   *
   * Overwrite this method with custom logic in your components.
   *
   * ?> Use this method to set child components by [setRef](#setRef) and run any preparatory work on the instance.
   */
  ;

  _proto.initialize = function initialize() {}
  /**
   * Lifecycle hook called after instance initialization.
   *
   * At this stage State and event binding are already in place.
   *
   * Overwrite this method with custom logic in your components.
   *
   * ?> `ready` lifecycle can be delayed (_async ready_) by implementing a [`readyState`](packages/yuzu/#async-ready-state) method.
   */
  ;

  _proto.ready = function ready() {}
  /**
   * Lifecycle hook called just before closing child refs.
   *
   * This hook is called just before destroying the instance. Every property, listener and state feature is still active.
   *
   * Overwrite this method with custom logic in your components.
   *
   * !> This is an async method. Return a promise in order to suspend the destroy process.
   */
  ;

  _proto.beforeDestroy = function beforeDestroy() {}
  /* eslint-enable @typescript-eslint/no-empty-function */

  /**
   * Returns an array of elements matching a CSS selector in the context of the component's root element
   *
   * @param selector {string} CSS selector to match
   * @return {Element[]}
   */
  ;

  _proto.findNodes = function findNodes(selector) {
    return qsa(selector, this.$el);
  }
  /**
   * Returns the first element matching a CSS selector in the context of the component's root element
   *
   * @param selector {string} CSS selector to match
   * @return {Element}
   */
  ;

  _proto.findNode = function findNode(selector) {
    return qs(selector, this.$el);
  }
  /**
   * ```js
   * getState(key)
   * ```
   *
   * Returns a property of the state or a default value if the property is not set.
   *
   * ?> In ES6 environments you can use a [destructuring assignment](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment) instead: ` const { name = 'John'} = this.state`
   *
   *
   * @param {string} key Property to return
   * @param {*} def Default value
   * @returns {*}
   * @example
   * const instance = new Component().mount('#app', { a: 1 });
   * // instance.getState('a') === 1
   *
   * // instance.getState('b', false) === false
   */
  ;

  _proto.getState = function getState(key, def) {
    return this.state.hasOwnProperty(key) ? this.state[key] : def;
  }
  /* eslint-disable class-methods-use-this */

  /**
   * ```js
   * shouldUpdateState(string, currentValue, newValue)
   * ```
   * Executes a strict inequality comparison (`!==`) on the passed-in values and returns the result.
   *
   * !> This method is executed on every [`setState`](#setstate) call.
   *
   * You can overwrite this method with your own validation logic.
   *
   * @param {string} key State property name
   * @param {*} currentValue Value stored in the current state
   * @param {*} newValue New value
   * @returns {boolean}
   */
  ;

  _proto.shouldUpdateState = function shouldUpdateState(_key, currentValue, newValue) {
    return currentValue !== newValue;
  }
  /* eslint-enable class-methods-use-this */

  /**
   * ```js
   * setState(updater, [silent])
   * ```
   *
   * Updates the internal instance state by creating a shallow copy of the current state and updating the passed-in keys.
   *
   * If the computed new state is different from the old one it emits a `change:<property>` event for every changed property
   * as well as a global `change:*` event.
   *
   * If the new value argument is a function, it will be executed with the current state as argument.
   * The returned value will be used to update the state.
   *
   * ?> To prevent an instance from emitting `change:` events set the second argument to `true` (silent update).
   *
   * @param {object|function} updater Defines which part of the state must be updated
   * @param {boolean} [silent=false] Update the state without emitting change events
   * @example
   * instance.on('change:a', (next, prev) => console.log(next, prev));
   * instance.setState({ a: 1 }); //emits 'change:a' -> logs undefined,1
   *
   * instance.setState({ a: 1 }); //nothing happens
   * instance.setState({ a: 2 }, true); //nothing happens, again...
   *
   * // use the current state to calculate its next value
   * instance.setState(({ a }) => ({a + 1}));
   */
  ;

  _proto.setState = function setState(updater, silent) {
    var _this4 = this;

    if (silent === void 0) {
      silent = false;
    }

    var changed = [];
    var prevState = this.state;
    var changeSet = evaluate(updater, this.state);

    if (process.env.NODE_ENV !== 'production') {
      objDiff(this.state, changeSet, function (k, keys) {
        return "setState: key \"" + k + "\" has been discarded because it is not defined in the component's initial state. Accepted keys are: " + keys;
      });
    }

    var entries = Object.entries(this.state);
    this.state = entries.reduce(function (newState, _ref5) {
      var k = _ref5[0],
          prevValue = _ref5[1];
      var value = changeSet[k];

      if (value === undefined || _this4.shouldUpdateState(k, prevValue, value) === false) {
        newState[k] = prevValue; // eslint-disable-line no-param-reassign
      } else {
        changed.push(k);
        newState[k] = value; // eslint-disable-line no-param-reassign
      }

      return newState;
    }, {});

    if (!silent && changed.length > 0) {
      while (changed.length > 0) {
        var k = changed.pop();
        this.emit("change:" + k, this.state[k], prevState[k]);
      }

      this.emit('change:*', this.state, prevState);
    }
  }
  /**
   * ```js
   * replaceState(newState, [silent])
   * ```
   *
   * Replaces the current state of the instance with a completely new state.
   *
   * !> Note that this methods is un-affected by [`shouldUpdateState`](#shouldupdatestate).
   *
   * @param {object} newState The new state object
   * @param {boolean} [silent=false] Replace the state without emitting change events
   * @example
   * instance.replaceState({ a: 1 });
   * // instance.state.a === 1
   * instance.replaceState({ b: 2 });
   * // instance.state.b === 2
   * // instance.state.a === undefined
   */
  ;

  _proto.replaceState = function replaceState(newState, silent) {
    var _this5 = this;

    if (silent === void 0) {
      silent = false;
    }

    var prevState = this.state;
    this.state = Object.assign({}, newState);
    var entries = Object.entries(this.state);
    entries.forEach(function (_ref6) {
      var key = _ref6[0],
          value = _ref6[1];

      if (!silent) {
        _this5.emit("change:" + key, value, prevState[key]);
      }
    });

    if (!silent) {
      this.emit('change:*', this.state, prevState);
    }
  }
  /**
   * ```js
   * broadcast(event, [...params])
   * ```
   *
   * Emits a `broadcast:<eventname>` event on every child component listed in `$refs`.
   *
   * @param {string} event Event name
   * @param {*[]} [params] Additional arguments to pass to the handler
   * @example
   * const child = new Component('#child');
   * child.on('broadcast:log', (str) => console.log(str));
   *
   * instance.setRef({ id: 'child', component: child });
   * instance.broadcast('log', 'test') // child component logs 'test'
   */
  ;

  _proto.broadcast = function broadcast(event) {
    for (var _len = arguments.length, params = new Array(_len > 1 ? _len - 1 : 0), _key2 = 1; _key2 < _len; _key2++) {
      params[_key2 - 1] = arguments[_key2];
    }

    this.$refsStore.forEach(function (instance) {
      instance.emit.apply(instance, ["broadcast:" + event].concat(params));
    });
  }
  /**
   * ```js
   * setListener(string, handler)
   * ```
   *
   * Sets a DOM event listener.
   *
   * The first argument must be a string composed by an event name (ie `click`) and a CSS selector (`.element`)
   * separated by a space.
   *
   * If the CSS selector starts with `@` the listener will be attached to the
   * corresponding reference child element (`this.$els.<element>`), if any.
   *
   * @param {string} def Event and target element definition. Format `eventName [target]`
   * @param {function} handler Event handler
   * @example
   *
   * // attach a click handler to a child element
   * instance.setListener('click .button', () => ...)
   *
   * // attach a click handler to this.$els.btn
   * instance.setListener('click @btn', () => ...)
   *
   * // attach a click handler to this.$el
   * instance.setListener('click', () => ...)
   */
  ;

  _proto.setListener = function setListener(def, handler) {
    var _this6 = this;

    var event;
    var selector;
    var match = def && def.match(LISTENER_REGEXP);

    if (match) {
      var $el = this.$el;
      event = match[1];
      var _match$ = match[2];
      selector = _match$ === void 0 ? $el : _match$;
      var element;

      if (typeof selector === 'string') {
        element = selector.startsWith('@') ? this.$els[selector.slice(1)] : qs(selector, $el);
      } else {
        element = selector;
      }

      if (element) {
        if (Array.isArray(element)) {
          element.forEach(function (el, i) {
            var h = function h(e) {
              handler.call(_this6, e, i);
            };

            el.addEventListener(event, h);

            _this6.$listeners.set(h, {
              event: event,
              element: el
            });
          });
        } else {
          element.addEventListener(event, handler);
          this.$listeners.set(handler, {
            event: event,
            element: element
          });
        }
      }
    }
  }
  /**
   * ```js
   * removeListeners()
   * ```
   *
   * Removes all DOM event listeners attached with `.setListener`.
   *
   */
  ;

  _proto.removeListeners = function removeListeners() {
    this.$listeners.forEach(function (_ref7, handler) {
      var event = _ref7.event,
          element = _ref7.element;
      element.removeEventListener(event, handler);
    });
    this.$listeners.clear();
  }
  /**
   * ```js
   * setRef(config, [props])
   * ```
   *
   * Attaches a reference to a child component.
   *
   * If a reference `id` is already attached, the previous one is destroyed and replaced with the new one.
   *
   * ?> This is an async method returning a promise.
   *
   * @param {object} config A child component configuration object
   * @param {string} config.id Reference id. Will be used to set a reference to the child component onto `this.$refs`
   * @param {component} config.component Component constructor or component instance
   * @param {string|HTMLElement} [config.el] Child component root element. This property is ignored if `config.component` is a component instance or a detached component constructor
   * @param {Object} [config.on] Child component event listeners. Format `{ 'eventname': handler }`
   * @param {*} config.* Any other property listed here will be passed to the constructor as option
   * @param {object} [props] Child component initial state
   * @returns {Promise}
   * @example
   * const parent = new Component('#root');
   *
   * class ChildComponent extends Component {}
   *
   * // as constructor
   * parent.setRef({
   *   id: 'child',
   *   component: ChildComponent,
   *   el: '#child',
   *   // other options here...
   * });
   *
   * // as instance
   * parent.setRef({
   *   id: 'child',
   *   component: new ChildComponent({ ... }).mount('#child', null) // <-- prevent component init
   * });
   *
   * // sync parent - child state
   * // updates child `parentCount` state whenever `parent.state.count` changes
   * parent.setRef({
   *   id: 'child',
   *   component: ChildComponent,
   *   el: '#child',
   *   // other options here...
   * }, {
   *   parentCount: (parentState) => parentState.count
   * });
   */
  ;

  _proto.setRef = function setRef(refCfg, props) {
    try {
      var _temp3 = function _temp3(_result) {
        if (_exit2) return _result;

        if ($el && ref.$el && !$el.contains(ref.$el)) {
          $el.appendChild(ref.$el);
        }

        return ref.init(refState);
      };

      var _exit2 = false;

      var _this8 = this;

      !isPlainObject(refCfg) ? process.env.NODE_ENV !== "production" ? invariant(false, 'Invalid reference configuration') : invariant(false) : void 0;

      var ChildComponent = refCfg.component,
          el = refCfg.el,
          id = refCfg.id,
          on = refCfg.on,
          options = _objectWithoutPropertiesLoose(refCfg, ["component", "el", "id", "on"]);

      var detached = _this8.detached;
      var ref; // if (el && detached) {
      //   throw new Error(
      //     `setRef "${id}": you cannot define a component with DOM root as child of a detached component.`,
      //   );
      // }

      if (Component.isComponent(ChildComponent)) {
        ref = new ChildComponent(options);
      } else if (ChildComponent instanceof Component) {
        ref = ChildComponent;
      } else if (typeof ChildComponent === 'function') {
        ref = ChildComponent(_this8.$el, _this8.state);
      } else {
        throw new TypeError('Invalid reference configuration');
      } // $context gets propagated to every child component
      // this way we can use `connect`-ed components as reference


      if (_this8.$context) {
        Object.defineProperty(ref, '$context', {
          enumerable: false,
          get: function get() {
            return _this8.$context;
          }
        });
      }

      Object.defineProperty(ref, '$parent', {
        writable: true,
        enumerable: false,
        value: _this8
      });
      !!!id ? process.env.NODE_ENV !== "production" ? invariant(false, 'Invalid reference id string') : invariant(false) : void 0; // bind events...

      if (on) {
        Object.entries(on).forEach(function (_ref8) {
          var name = _ref8[0],
              handler = _ref8[1];
          ref.on(name, handler);
        });
      }

      var $refs = _this8.$refs;
      var prevRef = $refs[id];
      var refState = {};
      $refs[id] = ref;

      _this8.$refsStore.set(id, ref);

      if (!ref.detached && !ref.$el) {
        !!!el ? process.env.NODE_ENV !== "production" ? invariant(false, "You need to provide a root element for the child element with id \"" + id + "\".") : invariant(false) : void 0;
        ref.mount(el, null);
      }

      var stateMap = evaluate(props, ref, _this8);

      if (stateMap) {
        Object.entries(stateMap).forEach(function (_ref9) {
          var name = _ref9[0],
              value = _ref9[1];

          if (typeof value === 'function') {
            var key = name;
            var src = '*';

            if (name.includes('>')) {
              var _name$split = name.split('>');

              var _name$split$ = _name$split[0];
              src = _name$split$ === void 0 ? '*' : _name$split$;
              key = _name$split[1];
            }

            refState[key] = value(src !== '*' ? _this8.state[src] : _this8.state, ref);

            _this8.on("change:" + src, function (state) {
              var _ref$setState;

              ref.setState((_ref$setState = {}, _ref$setState[key] = value(state, ref), _ref$setState));
            });
          } else {
            refState[name] = value;
          }
        });
      }

      var $el = _this8.$el;

      if (detached && ref.$el) {
        var $parent = _this8.$parent;

        while (!$el && $parent) {
          $el = $parent.$el;
          $parent = $parent.$parent;
        }
      }

      if (ref.$el) {
        !!!$el ? process.env.NODE_ENV !== "production" ? invariant(false, "You cannot attach a plain Component to a DetachedComponents tree. (" + id + ")") : invariant(false) : void 0;
      }

      var _temp4 = function () {
        if (prevRef) {
          return Promise.resolve(prevRef.destroy()).then(function () {
            if (prevRef.$el && $el.contains(prevRef.$el) && prevRef.$el.parentElement && ref.$el) {
              prevRef.$el.parentElement.replaceChild(ref.$el, prevRef.$el);
            } else if (ref.$el && !$el.contains(ref.$el)) {
              $el.appendChild(ref.$el);
            }

            _exit2 = true;
            return ref.init(refState);
          });
        }
      }();

      return Promise.resolve(_temp4 && _temp4.then ? _temp4.then(_temp3) : _temp3(_temp4));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Destroys and detaches a specific child component by its reference `id` (as set in `setRef`).
   *
   * @param {string} id Child component reference id
   * @param {boolean} [detach=false] Remove the child component root element from the DOM
   * @returns {Promise}
   */
  ;

  _proto.destroyRef = function destroyRef(id, detach) {
    if (detach === void 0) {
      detach = false;
    }

    try {
      var _this10 = this;

      var $refsStore = _this10.$refsStore;
      var ref = $refsStore.get(id);
      !ref ? process.env.NODE_ENV !== "production" ? invariant(false, "Child component \"" + id + "\" not found.") : invariant(false) : void 0;

      if (!ref) {
        return Promise.resolve();
      }

      $refsStore["delete"](id);
      delete _this10.$refs[id];

      if (!detach) {
        return Promise.resolve(ref.destroy());
      }

      return Promise.resolve(ref.destroy().then(function () {
        if (ref.$el) {
          ref.$el.remove();
        }
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Calls `.destroy()` on every child references and detaches them from the parent component.
   *
   * !> This is an async method returning a promise
   *
   * @returns {Promise}
   */
  ;

  _proto.destroyRefs = function destroyRefs() {
    try {
      var _this12 = this;

      var $refsStore = _this12.$refsStore;

      if ($refsStore.size === 0) {
        // exit early!
        return Promise.resolve();
      }

      return Promise.resolve(_catch(function () {
        var teardown = [];
        $refsStore.forEach(function (ref) {
          teardown.push(ref.destroy());
        });
        return Promise.resolve(Promise.all(teardown)).then(function (result) {
          _this12.$refs = {};
          $refsStore.clear();
          return result;
        });
      }, function (e) {
        if (process.env.NODE_ENV !== 'production') {
          _this12.$warn('An error occurred while destroy the component child components', e);
        }

        return Promise.reject(e);
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * ```js
   * destroy()
   * ```
   *
   * Detaches DOM events, instance's events and destroys all references as well.
   *
   * > **Lifecycle**
   * >
   * > | stage    | hooks     |
   * > |----------| --------- |
   * > | `destroy` | `beforeDestroy` |
   *
   *
   * !> This is an async method returning a promise
   *
   * @returns {Promise}
   */
  ;

  _proto.destroy = function destroy() {
    try {
      var _this14 = this;

      return Promise.resolve(_this14.beforeDestroy()).then(function () {
        _this14.removeListeners();

        if (_this14.$parent) {
          // clear the reference to the parent component
          _this14.$parent = undefined;
        }

        _this14.off();

        if (_this14.$el) {
          _this14.$el.removeAttribute(Component.YUZU_DATA_ATTR); // eslint-disable-line no-console

        }

        return _catch(function () {
          return Promise.resolve(_this14.destroyRefs()).then(function () {
            _this14.$active = false;
            return Promise.resolve();
          });
        }, function (e) {
          _this14.$warn('destroy error: ', e);

          return Promise.reject(e);
        });
      });
    } catch (e) {
      return Promise.reject(e);
    }
  };

  return Component;
}(Events);
/**
 * ```js
 * Component.YUZU_DATA_ATTR
 * ```
 * Component root element attribute marker.
 *
 * @static
 * @returns {object}
 */

Component.YUZU_DATA_ATTR = 'data-yuzu';
/**
 * Marks yuzu components
 *
 * @static
 */

Component.YUZU_COMPONENT = true;

/**
 * `DetachedComponent` is a special kind of `Component` that does not have a root element (`$el`).
 *
 * @class
 * @see [Component](/packages/yuzu/api/component)
 * @returns {DetachedComponent}
 */

var DetachedComponent =
/*#__PURE__*/
function (_Component) {
  _inheritsLoose(DetachedComponent, _Component);

  function DetachedComponent() {
    var _this;

    _this = _Component.apply(this, arguments) || this;
    _this.detached = true;
    return _this;
  }

  return DetachedComponent;
}(Component);

/**
 * Element array Iterator.
 *
 * The first argument can be:
 *
 * - a CSS selector. Element will be matched in the context of the passed-in parent's [`$el`](/packages/yuzu/api/component#instance-properties) property.
 * - an array of elements
 * - a function receiving the parent's root element and returning an array of element.
 *
 * The second parameter is an iterator function receiving the current element and its index as arguments.
 *
 * Returns a function that accepts a parent component as first argument and iterates the iterator function over an array of DOM elements.
 *
 * @param {string|Element[]} selector Elements CSS selector, array of elements or a function returning an array of elements.
 * @param {function} fn Iterator function
 * @example
 * import { Children } from 'yuzu';
 *
 * const parent = new ParentComponent().mount('#list');
 * const iterator = (el, i) => new ChildComponent(el, { index: i });
 *
 * const childComponentArray = Children('.items', iterator)(parent);
 *
 * // same as
 * // Children((el) => Array.from(el.querySelectorAll('.items')), iterator)(parent);
 *
 * // same as
 * // const els = Array.from(el.querySelectorAll('.items'))
 * // Children(els , iterator)(parent);
 */

var Children = function Children(selector, fn) {
  return function (ctx) {
    var els;

    if (typeof selector === 'string') {
      els = qsa(selector, ctx.$el);
    } else if (typeof selector === 'function') {
      els = selector(ctx.$el);
    } else if (Array.isArray(selector)) {
      els = selector;
    } else {
      throw new TypeError('Invalid selector argument');
    }

    return els.map(fn);
  };
};

var childRefIdx =
/*#__PURE__*/
createSequence();
/**
 * `mount` is an helper function to setup trees of components in a functional way.
 *
 * Returns a mount function which in turn accepts a parent component. If present, the returning instance will be attached as reference to the parent component.
 *
 * Child components listed in the `children` parameter will be automatically set as references in the component (uses: [`Component#setRef`](/packages/yuzu/api/component#setref))
 *
 * See the guide on **[functional composition](/packages/yuzu/#functional-composition)** for implementation examples.
 *
 * @param {Component} ComponentConstructor A component constructor (either created by extending `Component` or by [`extend`](/packages/yuzu/api/extend))
 * @param {HTMLElement|string|null} el A mount DOM node (either a CSS selector string or a DOM element). Can be `null` if the component is a [detached component](/packages/yuzu/#detached-components)
 * @param {function[]|function} [children] Child components. Either an array of `mount` functions or a function returning an array of mount functions
 * @param {object} [props] Mount props
 * @param {string} [props.id] Optional component id (used to create a reference onto the parent component)
 * @param {object} [props.state] Component initial state
 * @param {*} [props.*] every other property will be passed as instance option
 * @return {function}
 */

function mount(ComponentConstructor, el, props, children) {
  if (props === void 0) {
    props = {};
  }

  var _ref = props || {},
      _ref$state = _ref.state,
      state = _ref$state === void 0 ? {} : _ref$state,
      id = _ref.id,
      _ref$on = _ref.on,
      on = _ref$on === void 0 ? {} : _ref$on,
      options = _objectWithoutPropertiesLoose(_ref, ["state", "id", "on"]);

  var component = new ComponentConstructor(options);
  return function mounter(ctx) {
    if (!component.detached) {
      var root = typeof el === 'string' ? qs(el, ctx && ctx.$el) : el;

      if (isElement(root)) {
        component.mount(root, ctx ? null : state);
      }
    } else if (component.detached && !ctx) {
      component.init(state);
    }

    if (ctx) {
      ctx.setRef({
        component: component,
        id: id || childRefIdx(ctx.$uid + '-r.'),
        on: evaluate(on, ctx)
      }, state);
    }

    var childrenList = Array.isArray(children) ? children : [];

    if (typeof children === 'function') {
      childrenList.push.apply(childrenList, children(component));
    }

    for (var i = 0, l = childrenList.length; i < l; i += 1) {
      var child = childrenList[i];
      child(component);
    }

    return component;
  };
}

/**
 * Utility method to create new Component classes in environments that don't support ES6 `class`es.
 *
 * @param {Component} parent Component class to extend
 * @param {object} props New component default properties and methods
 * @example
 * // UMD environment
 * const Text = YZ.extend(YZ.Component, {
 *  created: function () {
 *    this.state = { body:  '' };
 *  },
 *  mounted: function () {
 *    this.$el.innerHTML = this.state.body;
 *  }
 * });
 */
function extend(parent, props) {
  if (props === void 0) {
    props = {};
  }

  var child = props.hasOwnProperty('constructor') ? props.constructor : function ChildConstructor(options) {
    return parent.call(this, options);
  }; // https://github.com/mridgway/hoist-non-react-statics/blob/master/index.js#L51

  var keys = Object.getOwnPropertyNames(parent);

  for (var i = 0, l = keys.length; i < l; i += 1) {
    var key = keys[i];
    var descriptor = Object.getOwnPropertyDescriptor(parent, key);

    try {
      // Avoid failures from read-only properties
      Object.defineProperty(child, key, descriptor);
    } catch (e) {}
  }

  child.prototype = Object.assign(Object.create(parent.prototype), props);
  child.prototype.constructor = child;
  child.__super__ = parent.prototype;
  return child;
}

/**
 * `devtools` is an helper function that will expose the instance of a Component in a `$yuzu` property attached to its root DOM element.
 *
 * It will also extend every Component instance with some useful methods.
 *
 * !> To improve performance in production, this property and its methods will be available just when `process.env.NODE_ENV !== 'production'`.
 *
 * To initialize the devtools copy the following code into your entry point:
 *
 * ```js
 * import { Component, devtools } from 'yuzu';
 *
 * devtools(Component);
 * ```
 */

var devtools;

if (process.env.NODE_ENV !== 'production') {
  /**
   *
   * @private
   * @param {string} label Logger name
   */
  var createStateLogger = function createStateLogger(label) {
    var $listeners = new Map();
    /**
     * @namespace logger
     * @private
     */

    return {
      label: label,

      /**
       * Subscribes to a specific event and logs its arguments when emitted. Returns an unsubscribe function.
       *
       * @private
       * @see logger#log
       * @param {Component} instance Target instance
       * @param {string} [event='change:*'] Event to subscribe to
       * @returns {function}
       */
      subscribe: function subscribe(instance, event) {
        var _this = this;

        if (event === void 0) {
          event = 'change:*';
        }

        var key = event === 'change:*' ? 'change' : event;
        var listener = this.log.bind(this, key);

        if ($listeners.has(event)) {
          console.warn("[yuzu-devtools:" + this.label + "]Already listening for \"" + event + "\" on " + instance.$uid);
        }

        instance.on(event, listener);
        $listeners.set(event, listener);
        return function () {
          _this.unsubscribe(instance, event);
        };
      },

      /**
       * Unsubscribes the logger from a specific event.
       *
       * @private
       * @param {Component} instance Target instance
       * @param {string} [event='change:*'] Event to subscribe to
       */
      unsubscribe: function unsubscribe(instance, event) {
        if (event === void 0) {
          event = 'change:*';
        }

        if ($listeners.has(event)) {
          instance.off(event, $listeners.get(event));
          $listeners["delete"](event);
        }
      },

      /**
       * Unsubscribes the logger from every event.
       *
       * @private
       * @param {Component} instance Target instance
       */
      unsubscribeAll: function unsubscribeAll(instance) {
        $listeners.forEach(function (listener, event) {
          instance.off(event, listener);
        });
        $listeners.clear();
      },

      /**
       * Logs formatted data.
       *
       * @private
       * @param {string} msg Log message
       * @param {*} next The current value
       * @param {*} [prev] An optional previous value
       * @param {*[]} args Additional arguments
       */
      log: function log(msg, next, prev, args) {
        if (process.env.NODE_ENV !== 'production') {
          var _console;

          var head = ["%c" + label + ": %c" + msg, 'color: gray; font-weight: lighter', 'color: green; font-weight: bolder'];

          if (args && args.length > 0) {
            head.push.apply(head, args);
          }

          (_console = console).groupCollapsed.apply(_console, head);

          if (prev !== undefined) {
            console.log('%cprev state', 'color: gray; font-weight: bolder', prev);
            console.log('%cnext state', 'color: green; font-weight: bolder', next);
          } else {
            console.log('%cinitial state', 'color: gray; font-weight: bolder', next);
          }

          console.groupEnd();
        }
      }
    };
  };
  /* eslint-disable no-param-reassign */


  devtools = function devtools(ComponentClass) {
    /**
     * Methods and properties added by devtools
     *
     * @name Component
     */
    var proto = ComponentClass.prototype;
    var mount = proto.mount,
        init = proto.init,
        destroy = proto.destroy;

    proto.mount = function mountDev() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      mount.call.apply(mount, [this].concat(args));
      /**
       * A reference to a Component instance attached to its root element.
       *
       * @name $el.$yuzu
       * @type Component
       * @memberof Component
       */

      Object.defineProperty(this.$el, '$yuzu', {
        value: this,
        configurable: true
      });
      return this;
    };

    proto.destroy = function destroyDev() {
      var $el = this.$el;

      if ($el && $el.$yuzu) {
        delete $el.$yuzu;
      }

      delete this.$$logStart;
      delete this.$$logEnd;
      return destroy.call(this);
    };

    proto.init = function initDev(state) {
      Object.defineProperties(this, {
        /**
         * ```js
         * $$logStart([label], [event])
         * ```
         *
         * Will initialize an event logger with a custom label. By default will automatically log any change to the instance `state` property.
         *
         * ?> Note that event loggers will be not available until the instance is initialized
         *
         * @memberof Component
         * @param {string} [label] Log label. If not defined will fallback to: A `debugLabel` option, a static `displayName` property defined on the constructor, the component name
         * @param {string|boolean} [listen="change:*"] Event to listen for or `false`.
         */
        $$logStart: {
          enumerable: false,
          configurable: true,
          value: function value(label, listen) {
            if (label === void 0) {
              label = null;
            }

            if (listen === void 0) {
              listen = 'change:*';
            }

            var name = label || this.options.debugLabel || (this.constructor.displayName || this.constructor.name || 'Component') + "#" + this.$uid;

            if (!this.$$logger) {
              this.$$logger = createStateLogger(name);
            }

            if (listen) {
              this.$$logger.subscribe(this, listen);
            }
          }
        },

        /**
         * ```js
         * $$logEnd([event])
         * ```
         *
         * Will stop an event logger either for a specific event or for every event.
         *
         * @memberof Component
         * @param {string} [event] Event to unsubscribe for. If not provided the logger will be completely stopped and removed
         */
        $$logEnd: {
          enumerable: false,
          configurable: true,
          value: function value(event) {
            if (this.$$logger) {
              if (event) {
                this.$$logger.unsubscribe(this, event);
                return;
              }

              this.$$logger.unsubscribeAll(this);
              this.$$logger = undefined;
            }
          }
        }
      });
      return init.call(this, state);
    };

    if (window.__YUZU_DEVTOOLS_GLOBAL_HOOK__) {
      window.__YUZU_DEVTOOLS_GLOBAL_HOOK__.init(Component);
    }
  };
  /* eslint-enable no-param-reassign */

} else {
  devtools = function devtools() {
    return undefined;
  };
}

export { Children, Component, DetachedComponent, devtools, extend, mount };
//# sourceMappingURL=yuzu.esm.js.map
